import { useCallback, useEffect, useState } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import AppContext from './context/AppContext';
import { NavigationProvider } from './context/NavigationContext';
import Layout from './components/Layout';
import TelegramRequiredScreen from './components/TelegramRequiredScreen';
import SplashScreen from './SplashScreen';

// Import components - utiliser la version simplifiée de Stories pour éviter l'erreur
import StoriesMock from './stories-mock';
import Profile from './composant/Profile';
import Settings from './composant/Settings';
import KappelaApp from './composant/KappelaApp';
import Plans from './composant/plans/Plans';

// Import groupes et canaux components
import RelierBot from './composant/groupesCannaux/RelierBot';
import GroupList from './composant/groupesCannaux/GroupList';
import GroupDetails from './composant/groupesCannaux/GroupDetails';

// Import produits components
import ListeProduit from './composant/produits/ListeProduit';
import AjouterProduit from './composant/produits/AjouterProduit';
import ProductsList from './composant/produits/ProductsList';
import ProductCard from './composant/produits/ProductCard';
import ModifierProduit from './composant/produits/ModifierProduit';

// Import méthodes de paiement components
import ActivationMethode from './composant/methodesPaiement/ActivationMethode';
import ModifierKey from './composant/methodesPaiement/ModifierKey';
import PaymentSheet from './composant/methodesPaiement/PaymentSheet';
import AjouterMethode from './composant/methodesPaiement/AjouterMethode';
import PaymentsSheetsIndex from './composant/methodesPaiement/PaymentsSheetsIndex';
import ModifierKeyIndex from './composant/methodesPaiement/ModifierKeyIndex';
import SuccesBottomSheetIndex from './composant/methodesPaiement/SuccesBottomSheetIndex';

// Import paiement components
import PaymentStatus from './composant/payement/PaymentStatus';
import IndexPaiement from './composant/payement/IndexPaiement';
import PaymentSelector from './composant/payement/PaymentSelector';
import MonerooPaymentStatus from './composant/payement/MonerooPaymentStatus';
import PaymentStatusFedapay from './composant/payement/fedapay/PaymentStatusFedapay';
import PaymentStatusFedapayFalse from './composant/payement/fedapay/PaymentStatusFedapayFalse';

// Import achat, espace, historiques components
import Achat from './composant/achat/Achat';
import Soni from './composant/achat/Soni';
import IndexEspace from './composant/espace/IndexEspace';
import CustomPaymentConfig from './composant/espace/CustomPaymentConfig';
import Historique from './composant/historiques/Historique';
import IndexPaiementAbonnement from './composant/achat/IndexPaiementAbonnement';
import Retrait from './composant/retrait/Retrait';
import ConfigurationRetrait from './composant/retrait/ConfigurationRetrait';
import TransactionHistory from './composant/transactions/TransactionHistory';

// Import faq components
import IndexFaq from './composant/faq/IndexFaq';
import CustomerSupport from './composant/faq/CustomerSupport';
import Statistics from './composant/statistics/Statistics';

// Nouvelle page dédiée à la configuration des paiements pour éviter l'erreur de constructeur
const PaymentConfigPage = () => {
  const { setShowPaymentSheet } = useContext(AppContext);
  const navigate = useNavigate();
  
  const handleShowPaymentMethods = () => {
    navigate('/PaymentSheet');
  };
  
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-md mx-auto bg-white rounded-xl shadow-sm p-6 mt-10">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Configuration des paiements</h1>
        
        <p className="text-gray-600 mb-6">
          Pour accepter des paiements, vous devez configurer au moins une méthode de paiement.
          Cela vous permettra de recevoir des paiements de vos clients.
        </p>
        
        <div className="bg-blue-50 p-4 rounded-lg mb-6">
          <p className="text-sm text-blue-700">
            🔒 Vos informations de paiement sont sécurisées et cryptées
          </p>
        </div>
        
        <div className="space-y-3">
          <button
            onClick={handleShowPaymentMethods}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium"
          >
            Configurer maintenant
          </button>
          
          <button
            onClick={() => navigate('/IndexEspace')}
            className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-medium"
          >
            Plus tard
          </button>
        </div>
      </div>
    </div>
  );
};

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, userInfos, isSplashVisible, ontg, splashError } = useContext(AppContext);
  
  const [history, setHistory] = useState([]);

  // Track navigation for history
  const handleNavigate = useCallback((route) => {
    setHistory((prev) => [...prev, route]);
  }, []);

  // Handle Telegram back button behavior - mais seulement pour les pages indépendantes
  // Le comportement pour les pages du Layout est géré dans le composant Layout
  const handleBackButton = useCallback((defaultBackPath = '/IndexEspace') => {
    // Utiliser un bloc try/catch pour éviter les erreurs avec window.Telegram
    try {
      // Ne pas utiliser l'accès direct à window.Telegram qui peut causer des erreurs
      // Utiliser la fonction setupTelegramBackButton du contexte à la place
      
      // Vérifier si l'utilisateur est sur une page du Layout (qui a la bottom bar)
      const mainRoutes = ['/', '/IndexEspace', '/ProductsList', '/GroupeListe', '/Statistics', '/PaymentsSheetsIndex'];
      const isMainRouteOrSubroute = location.pathname.startsWith('/ProductCard') || 
                                 location.pathname.startsWith('/GroupeDetail') ||
                                 mainRoutes.includes(location.pathname);
      
      // Si c'est une page du Layout, on laisse le Layout gérer le bouton retour
      if (isMainRouteOrSubroute) {
        return;
      }
      
      // Pour les autres pages, déterminer le comportement en fonction du type de page
      const isAndroid = /Android/i.test(navigator.userAgent);
      const isApple = /iPhone|iPad|iPod|MacIntel/i.test(navigator.userAgent);
      
      // Identifier les routes liées aux réglages pour les rediriger vers la page des réglages
      const paymentRoutes = [
        '/ActivationMethode',
        '/ModifierKey',
        '/PaymentSheet',
        '/AjouterMethode',
        '/ModifierKeyIndex',
        '/SuccesBottomSheetIndex',
        '/CustomPaymentConfig'
      ];
      
      // Utiliser le hook de navigation React Router à la place
      const handleClickBack = () => {
        // Pour les routes de paiement, rediriger vers PaymentsSheetsIndex
        if (paymentRoutes.includes(location.pathname)) {
          navigate('/PaymentsSheetsIndex');
        } else if (isAndroid) {
          // Pour les autres routes sur Android
          navigate(defaultBackPath);
        } else if (isApple) {
          // Utiliser la navigation native sur iOS/MacBook
          window.history.back();
        }
      };
      
      // Pour éviter l'erreur "Illegal constructor", on n'utilise pas directement window.Telegram.WebApp
      // Le contexte de l'application (AppContext) devrait fournir une fonction setupTelegramBackButton sécurisée
    } catch (error) {
      console.error("Erreur lors de la configuration du bouton retour:", error);
    }
  }, [navigate, location.pathname]);

  // Intégration et gestion du bouton retour Telegram
  useEffect(() => {
    // Track navigation history
    handleNavigate(location.pathname);
    
    // Gérer le bouton retour de Telegram pour les pages indépendantes
    const mainRoutes = ['/', '/IndexEspace', '/ProductsList', '/GroupeListe', '/Statistics', '/PaymentsSheetsIndex'];
    const isMainRouteOrSubroute = location.pathname.startsWith('/ProductCard') || 
                             location.pathname.startsWith('/GroupeDetail') ||
                             mainRoutes.includes(location.pathname);
    
    if (!isMainRouteOrSubroute) {
      handleBackButton();
    }
    
    return () => {};
  }, [location, handleNavigate, handleBackButton]);

  return (
    <NavigationProvider>
      {/* Si l'utilisateur n'est pas dans Telegram, afficher TelegramRequiredScreen */}
      {ontg === false ? <TelegramRequiredScreen /> : (
        <>
          {/* Si le splash screen est visible, on l'affiche en couche supérieure */}
          {isSplashVisible && <SplashScreen error={splashError} />}
          
          {/* Routes - toujours chargées mais masquées par le splash si nécessaire */}
          <Routes>
            {/* Routes qui utilisent la bottom navigation bar */}
          <Route element={<Layout />}>
            {/* Routes principales avec bottom bar */}
            <Route path="/" element={<IndexEspace />} />
            <Route path="/IndexEspace" element={<IndexEspace />} />
            <Route path="/ProductsList" element={<ProductsList />} />
            <Route path="/GroupeListe" element={<GroupList />} />
            <Route path="/Statistics" element={<Statistics />} />
            <Route path="/PaymentsSheetsIndex" element={<PaymentsSheetsIndex />} />
            
            {/* Sous-routes qui gardent la bottom bar */}
            <Route path="/ProductCard/:idProduit" element={<ProductCard />} />
            <Route path="/GroupeDetail/:idGroup/:type/:nom" element={<GroupDetails />} />
            
            {/* Ajouter les routes de réglages sous Layout pour qu'elles gardent la bottom bar */}
            <Route path="/ActivationMethode" element={<ActivationMethode />} />
            <Route path="/ModifierKey" element={<ModifierKey />} />
            <Route path="/PaymentSheet" element={<PaymentSheet />} />
            <Route path="/AjouterMethode" element={<AjouterMethode />} />
            <Route path="/ModifierKeyIndex" element={<ModifierKeyIndex />} />
            <Route path="/SuccesBottomSheetIndex" element={<SuccesBottomSheetIndex />} />
            <Route path="/CustomPaymentConfig" element={<CustomPaymentConfig />} />
            <Route path="/ConfigurerPaiement" element={<PaymentConfigPage />} />
          </Route>
        
          {/* Onboarding and independent routes - version sécurisée sans Lock */}
          <Route path="/stories" element={<StoriesMock />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/settings" element={<KappelaApp />} />
          <Route path="/Plans" element={<Plans />} />
          
          {/* Other independent routes */}
          <Route path="/relierbot" element={<RelierBot />} />
          <Route path="/listeProduit" element={<ListeProduit />} />
          <Route path="/ajouterProduit/:idGroup" element={<AjouterProduit />} />
          <Route path="/ModifierProduit/:idProduit" element={<ModifierProduit />} />
          
          {/* Payment process - independent */}
          <Route path="/IndexPaiement" element={<IndexPaiement />} />
          <Route path="/PaymentStatus" element={<PaymentStatus />} />
          <Route path="/MonerooPaymentStatus" element={<MonerooPaymentStatus />} />
          <Route path="/PaymentSelector" element={<PaymentSelector />} />
          <Route path="/PaymentStatusFedapay" element={<PaymentStatusFedapay />} />
          <Route path="/PaymentStatusFedapayFalse" element={<PaymentStatusFedapayFalse />} />
          <Route path="/IndexPaiementAbonnement" element={<IndexPaiementAbonnement />} />
          
          {/* Purchase and history - independent */}
          <Route path="/historique" element={<Historique />} />
          <Route path="/achat" element={<Achat />} />
          <Route path="/soni" element={<Soni />} />
          <Route path="/retrait" element={<Retrait />} />
          <Route path="/ConfigurationRetrait" element={<ConfigurationRetrait />} />
          <Route path="/transactions/history" element={<TransactionHistory />} />
          
          {/* Help and support - independent */}
          <Route path="/IndexFaq" element={<IndexFaq />} />
          <Route path="/CustomerSupport" element={<CustomerSupport />} />
        </Routes>
        </>
      )}
    </NavigationProvider>
  );
}